import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { Form, InputContainer } from "./styles";

import { Search } from "lucide-react";

import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../hooks/auth";
import refreshToken from "../../utils/refreshToken";
import { resolvePropertyNavigationId } from "../../utils/resolvePropertyNavigationId";

interface ISearchBarPropertiesPage {
  term: string;
  /**
   * Atualiza a URL da lista após o usuário digitar (debounce) e ao enviar o form
   * sem código de imóvel. Quem monta o path completo é o pai (ex.: history.replace).
   */
  onApplySearch?: (query: string) => void;
  /** Milissegundos após parar de digitar para aplicar a busca. Default 420. */
  debounceMs?: number;
  /**
   * Fallback quando `onApplySearch` não é passado: destino ao enviar o form (texto).
   * Must match the list route (e.g. `/properties/1`).
   */
  listBasePath?: string;
}

export default function SearchBarPropertiesPage({
  term,
  onApplySearch,
  debounceMs = 420,
  listBasePath = "/properties/1",
}: ISearchBarPropertiesPage) {
  const history = useHistory();
  const { access_token, dateToExpires } = useAuth();

  const [error, setError] = useState(false);
  const [resolvingCode, setResolvingCode] = useState(false);

  const [inputSearchCode, setIinputSearchCode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const skipDebounceRef = useRef(true);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Parent callbacks often change when route updates; must not retrigger debounce. */
  const onApplySearchRef = useRef(onApplySearch);
  onApplySearchRef.current = onApplySearch;

  useEffect(() => {
    skipDebounceRef.current = true;
    setSearchTerm(term ?? "");
  }, [term]);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (skipDebounceRef.current) {
      skipDebounceRef.current = false;
      return;
    }

    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onApplySearchRef.current?.(searchTerm);
    }, debounceMs);

    return clearDebounce;
  }, [searchTerm, debounceMs, clearDebounce]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      if (inputSearchCode && inputSearchCode.length > 0) {
        const requestedId = Number(inputSearchCode);
        if (!Number.isFinite(requestedId) || requestedId <= 0) {
          history.push(`/property/${inputSearchCode}`);
          return;
        }

        setResolvingCode(true);
        try {
          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
          const targetId = await resolvePropertyNavigationId(api, requestedId);
          history.push(`/property/${targetId}`);
        } finally {
          setResolvingCode(false);
        }
        return;
      }

      clearDebounce();

      if (onApplySearch) {
        onApplySearch(searchTerm);
        return;
      }

      const qs = new URLSearchParams();
      if (searchTerm) qs.set("search", searchTerm);
      const q = qs.toString();
      const base = listBasePath.replace(/\/$/, "");
      history.push(q ? `${base}?${q}` : base);
    } catch (err: unknown) {
      setError(true);
      setResolvingCode(false);
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <InputContainer thereIsAnError={error}>
          <input
            autoComplete="off"
            value={inputSearchCode}
            onChange={(e) => {
              setIinputSearchCode(e.target.value);
            }}
            type="text"
            placeholder="Código do imóvel"
            disabled={resolvingCode}
          />
        </InputContainer>
        <InputContainer thereIsAnError={error}>
          <input
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            type="text"
            placeholder="Informações do imóvel. Ex: bairro, rua, email do proprietário, andar"
            disabled={resolvingCode}
          />
        </InputContainer>
        <button type="submit" title="Buscar agora" disabled={resolvingCode}>
          <Search className="h-5 w-5 text-[#DD6435]" aria-hidden />
        </button>
      </Form>
      {onApplySearch ? (
        <p className="mt-2 text-xs text-slate-500">
          A lista atualiza automaticamente enquanto você digita.
        </p>
      ) : null}
    </>
  );
}
